import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHubspot } from "@fortawesome/free-brands-svg-icons";

import type {
  Audience,
  Session,
  Survey,
  TimestampLike,
  TravelItem,
} from "@event-planner/shared";

import { DataTable, type Column } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import {
  HubSpotMatchDialog,
  type HubSpotMatchTarget,
} from "../../components/HubSpotMatchDialog";
import { useEvent } from "../../layouts/EventLayout";
import { useAttendeeReport } from "../../hooks/useAttendeeReport";
import { getPersonInternal } from "../../services/personInternalService";
import {
  buildHubSpotContactUrl,
  isHubSpotConfigured,
  HUBSPOT_ORANGE,
} from "../../utils/hubspot";
import { traceMembership } from "../../utils/traceMembership";
import type { TraceMembershipIndex } from "../../utils/traceMembership";
import type { ReportNotificationEntry } from "../../services/attendeeReportService";
import { AttendeeReportSection } from "./AttendeeReportSection";
import { useIsPhone } from "../../hooks/useIsPhone";
import { AttributionChip } from "./AttributionChip";

dayjs.extend(utc);
dayjs.extend(tz);

const KIND_LABEL: Record<TravelItem["details"]["kind"], string> = {
  flight: "Flight",
  lodging: "Lodging",
  transport: "Transport",
  other: "Other",
};

function formatDateTime(ts: TimestampLike | null, timezone: string): string {
  if (!ts) return "—";
  return dayjs(ts.toDate()).tz(timezone).format("MMM D, h:mm A");
}

function travelTime(item: TravelItem): TimestampLike | null {
  const d = item.details;
  switch (d.kind) {
    case "flight":
      return d.departureAt ?? d.arrivalAt;
    case "lodging":
      return d.checkInAt;
    case "transport":
      return d.pickupAt ?? null;
    case "other":
      return d.time ?? null;
  }
}

function travelSummary(item: TravelItem): string {
  const d = item.details;
  switch (d.kind) {
    case "flight":
      return [d.airline, d.flightNumber].filter(Boolean).join(" ") || "Flight";
    case "lodging":
      return d.confirmationCode
        ? `Confirmation ${d.confirmationCode}`
        : "Lodging";
    case "transport":
      return d.mode;
    case "other":
      return d.title;
  }
}

export function AttendeeReportPage() {
  const isPhone = useIsPhone();
  const navigate = useNavigate();
  const { eventId, personId } = useParams<{
    eventId: string;
    personId: string;
  }>();
  const { event } = useEvent();

  const target = useMemo(() => {
    if (!eventId || !personId) return null;
    return { eventId, personId };
  }, [eventId, personId]);

  const { data, loading, error } = useAttendeeReport(target);

  const timezone = event?.timezone ?? "UTC";

  // HubSpot link state (ADR-172 review-first). One extra internals
  // read; linking itself happens on the person's edit form — the match
  // dialog below just hands the picked contact over via the query param.
  const [hubspotContactId, setHubspotContactId] = useState<string | null>(null);
  const [hubspotMatchTarget, setHubspotMatchTarget] =
    useState<HubSpotMatchTarget | null>(null);

  const refreshHubSpotLink = useCallback(() => {
    if (!personId || !isHubSpotConfigured()) return;
    getPersonInternal(personId)
      .then((internal) =>
        setHubspotContactId(internal?.hubspotContactId ?? null),
      )
      .catch(() => {
        // non-fatal — the button just stays in the unlinked state
      });
  }, [personId]);

  useEffect(() => {
    refreshHubSpotLink();
  }, [refreshHubSpotLink]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          Could not load the experience report. {error?.message}
        </Alert>
        <Button
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate(`/events/${eventId}/attendees`)}
          sx={{ mt: 2 }}
        >
          Back to Roster
        </Button>
      </Box>
    );
  }

  const { subject, sessions, travelItems, notifications, surveys, audiences } =
    data;

  const displayName = subject.person.displayName || subject.person.email;
  const email = subject.profile?.email ?? subject.person.email;
  const photoURL = subject.person.photoURL;
  const role = subject.participant?.role ?? subject.person.role;
  const showHubSpot = isHubSpotConfigured() && !!email;

  return (
    <Box>
      <PageHeader
        title={`Experience report — ${displayName}`}
        action={
          <Button
            startIcon={<ArrowLeft size={16} />}
            onClick={() => navigate(`/events/${eventId}/attendees`)}
          >
            Back to Roster
          </Button>
        }
      />

      {/* ── Header card ─────────────────────────────────────────────── */}
      {/* Phone: the card wraps, so the HubSpot and profile buttons drop
          to their own row under the name. */}
      <Paper sx={{ p: isPhone ? 2 : 3, mb: 4 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap={isPhone}
          flexWrap={isPhone ? "wrap" : undefined}
        >
          <Avatar src={photoURL ?? undefined} sx={{ width: 56, height: 56 }}>
            {displayName[0] ?? "?"}
          </Avatar>
          <Box sx={isPhone ? { flexGrow: 1, minWidth: 0 } : { flexGrow: 1 }}>
            <Typography variant="h6">{displayName}</Typography>
            {email && (
              <Typography variant="body2" color="text.secondary">
                {email}
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip label={role} size="small" />
              <Chip
                label={subject.isClaimed ? "Active" : "Pending"}
                size="small"
                color={subject.isClaimed ? "success" : "warning"}
                variant="outlined"
              />
            </Stack>
          </Box>
          {showHubSpot &&
            (hubspotContactId ? (
              <Button
                variant="outlined"
                size="small"
                endIcon={
                  <FontAwesomeIcon icon={faHubspot} style={{ fontSize: 14 }} />
                }
                href={
                  buildHubSpotContactUrl({
                    email,
                    contactId: hubspotContactId,
                  }) ?? ""
                }
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: HUBSPOT_ORANGE,
                  borderColor: HUBSPOT_ORANGE,
                  "&:hover": { borderColor: HUBSPOT_ORANGE },
                }}
                aria-label={`Open ${displayName} in HubSpot (linked)`}
                data-testid="open-hubspot-report-button"
              >
                HubSpot
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                endIcon={
                  <FontAwesomeIcon icon={faHubspot} style={{ fontSize: 14 }} />
                }
                onClick={() =>
                  setHubspotMatchTarget({
                    personId: subject.personId,
                    email,
                    displayName,
                  })
                }
                aria-label={`Not linked — find ${displayName} in HubSpot`}
                data-testid="open-hubspot-report-button"
              >
                HubSpot
              </Button>
            ))}
          <Button
            variant="outlined"
            size="small"
            endIcon={<ExternalLink size={14} />}
            component={RouterLink}
            to={`/users/${subject.personId}`}
          >
            Cross-event profile
          </Button>
        </Stack>
      </Paper>

      <HubSpotMatchDialog
        target={hubspotMatchTarget}
        onClose={() => setHubspotMatchTarget(null)}
        onConfirm={(suggestion) => {
          // Review happens on the person's edit form (ADR-172).
          navigate(
            `/users/${subject.personId}?hubspotContact=${suggestion.contactId}`,
          );
        }}
      />

      {!subject.isClaimed && (
        <Alert severity="info" sx={{ mb: 4 }}>
          This person hasn&apos;t signed in yet. Items below are linked through
          their audiences. Notification receipts will be backfilled on
          first activation.
        </Alert>
      )}

      <TravelSection
        eventId={eventId!}
        items={travelItems}
        traceIndex={data.traceIndex}
        timezone={timezone}
      />
      <AgendaSection
        eventId={eventId!}
        sessions={sessions}
        traceIndex={data.traceIndex}
        timezone={timezone}
      />
      <NotificationsSection
        eventId={eventId!}
        entries={notifications}
        traceIndex={data.traceIndex}
        timezone={timezone}
        isClaimed={subject.isClaimed}
      />
      <SurveysSection
        eventId={eventId!}
        surveys={surveys}
        notificationEntries={notifications}
        traceIndex={data.traceIndex}
      />
      <AudiencesSection
        eventId={eventId!}
        audiences={audiences}
        personalAudienceId={data.traceIndex.personalAudienceId}
      />
    </Box>
  );
}

// ── sections ─────────────────────────────────────────────────────────────

function TravelSection({
  eventId,
  items,
  traceIndex,
  timezone,
}: {
  eventId: string;
  items: TravelItem[];
  traceIndex: TraceMembershipIndex;
  timezone: string;
}) {
  const sorted = [...items].sort((a, b) => {
    const ta = travelTime(a)?.toDate().getTime() ?? Infinity;
    const tb = travelTime(b)?.toDate().getTime() ?? Infinity;
    return ta - tb;
  });
  const columns: Column<TravelItem>[] = [
    {
      id: "when",
      label: "When",
      width: "22%",
      render: (i) => formatDateTime(travelTime(i), timezone),
    },
    {
      id: "kind",
      label: "Kind",
      width: "12%",
      render: (i) => KIND_LABEL[i.details.kind],
    },
    {
      id: "summary",
      label: "Summary",
      render: (i) => travelSummary(i),
    },
    {
      id: "reminder",
      label: "Reminder",
      width: "14%",
      render: (i) =>
        i.reminder?.enabled
          ? `${i.reminder.leadMinutes} min ahead`
          : "—",
    },
    {
      id: "via",
      label: "Via",
      width: "22%",
      render: (i) => (
        <AttributionChip
          eventId={eventId}
          attribution={traceMembership(i.audienceId, traceIndex)}
        />
      ),
    },
  ];
  return (
    <AttendeeReportSection title="Travel" count={sorted.length}>
      <DataTable<TravelItem>
        columns={columns}
        data={sorted}
        loading={false}
        ariaLabel="Travel items"
        emptyMessage="No travel items linked to this attendee."
        getRowKey={(i) => i.id}
        onRowClick={(i) =>
          window.location.assign(`/events/${eventId}/travel/${i.id}`)
        }
      />
    </AttendeeReportSection>
  );
}

function AgendaSection({
  eventId,
  sessions,
  traceIndex,
  timezone,
}: {
  eventId: string;
  sessions: Session[];
  traceIndex: TraceMembershipIndex;
  timezone: string;
}) {
  const sorted = [...sessions].sort(
    (a, b) => a.startTime.toDate().getTime() - b.startTime.toDate().getTime(),
  );
  const columns: Column<Session>[] = [
    {
      id: "when",
      label: "When",
      width: "22%",
      render: (s) => formatDateTime(s.startTime, timezone),
    },
    {
      id: "title",
      label: "Title",
      render: (s) => s.title,
    },
    {
      id: "location",
      label: "Location",
      width: "18%",
      render: (s) => s.locationLabel ?? "—",
    },
    {
      id: "via",
      label: "Via",
      width: "22%",
      render: (s) => (
        <AttributionChip
          eventId={eventId}
          attribution={traceMembership(s.audienceId, traceIndex)}
        />
      ),
    },
  ];
  return (
    <AttendeeReportSection title="Agenda" count={sorted.length}>
      <DataTable<Session>
        columns={columns}
        data={sorted}
        loading={false}
        ariaLabel="Agenda sessions"
        emptyMessage="No sessions target this attendee."
        getRowKey={(s) => s.id}
        onRowClick={(s) =>
          window.location.assign(`/events/${eventId}/agenda/${s.id}`)
        }
      />
    </AttendeeReportSection>
  );
}

function NotificationsSection({
  eventId,
  entries,
  traceIndex,
  timezone,
  isClaimed,
}: {
  eventId: string;
  entries: ReportNotificationEntry[];
  traceIndex: TraceMembershipIndex;
  timezone: string;
  isClaimed: boolean;
}) {
  const delivered = entries.filter((e) => e.deliveryState === "delivered");
  const pending = entries.filter((e) => e.deliveryState !== "delivered");
  const subtitle =
    !isClaimed
      ? "Receipts will appear after this contact activates their account."
      : "Delivered shows notifications with a receipt for this user; Queued shows scheduled or unsent notifications targeting them.";

  return (
    <AttendeeReportSection
      title="Notifications"
      count={entries.length}
      subtitle={subtitle}
    >
      <NotificationGroup
        label="Delivered"
        rows={delivered}
        eventId={eventId}
        traceIndex={traceIndex}
        timezone={timezone}
        isClaimed={isClaimed}
        showWhenEmpty={isClaimed}
      />
      <Box sx={{ mt: 3 }}>
        <NotificationGroup
          label={
            !isClaimed ? "Will deliver on activation" : "Queued / scheduled"
          }
          rows={pending}
          eventId={eventId}
          traceIndex={traceIndex}
          timezone={timezone}
          isClaimed={isClaimed}
          showWhenEmpty={false}
        />
      </Box>
    </AttendeeReportSection>
  );
}

function NotificationGroup({
  label,
  rows,
  eventId,
  traceIndex,
  timezone,
  isClaimed,
  showWhenEmpty,
}: {
  label: string;
  rows: ReportNotificationEntry[];
  eventId: string;
  traceIndex: TraceMembershipIndex;
  timezone: string;
  isClaimed: boolean;
  showWhenEmpty: boolean;
}) {
  if (rows.length === 0 && !showWhenEmpty) return null;
  const columns: Column<ReportNotificationEntry>[] = [
    {
      id: "title",
      label: "Title",
      render: (e) => e.notification.title,
    },
    {
      id: "sent",
      label: "Sent",
      width: "18%",
      render: (e) => formatDateTime(e.notification.sentAt ?? null, timezone),
    },
    {
      id: "status",
      label: "Status",
      width: "16%",
      render: (e) => statusChip(e, isClaimed),
    },
    {
      id: "via",
      label: "Via",
      width: "22%",
      render: (e) => (
        <AttributionChip
          eventId={eventId}
          attribution={traceMembership(e.notification.audienceId, traceIndex)}
        />
      ),
    },
  ];
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <DataTable<ReportNotificationEntry>
        columns={columns}
        data={rows}
        loading={false}
        ariaLabel={label}
        emptyMessage="None."
        getRowKey={(e) => e.notification.id}
        onRowClick={(e) =>
          window.location.assign(
            `/events/${eventId}/notifications/${e.notification.id}`,
          )
        }
      />
    </Box>
  );
}

function statusChip(
  entry: ReportNotificationEntry,
  isClaimed: boolean,
) {
  if (entry.deliveryState === "delivered") {
    return entry.receipt?.readAt ? (
      <Chip label="Read" size="small" color="success" variant="outlined" />
    ) : (
      <Chip label="Delivered" size="small" color="info" variant="outlined" />
    );
  }
  if (!isClaimed && entry.notification.status === "sent") {
    return (
      <Chip label="Pending activation" size="small" color="warning" variant="outlined" />
    );
  }
  return (
    <Chip
      label={entry.notification.status}
      size="small"
      color="default"
      variant="outlined"
    />
  );
}

function SurveysSection({
  eventId,
  surveys,
  notificationEntries,
  traceIndex,
}: {
  eventId: string;
  surveys: Survey[];
  notificationEntries: ReportNotificationEntry[];
  traceIndex: TraceMembershipIndex;
}) {
  const notifById = new Map(
    notificationEntries.map((e) => [e.notification.id, e]),
  );
  const columns: Column<Survey>[] = [
    {
      id: "question",
      label: "Question",
      render: (s) =>
        s.questions.length === 1
          ? s.questions[0].prompt
          : `${s.questions[0]?.prompt ?? "(no question)"} (+${s.questions.length - 1} more)`,
    },
    {
      id: "status",
      label: "Status",
      width: "14%",
      render: (s) => (
        <Chip label={s.status} size="small" variant="outlined" />
      ),
    },
    {
      id: "anon",
      label: "Response visibility",
      width: "22%",
      render: () => (
        <Chip
          label="Anonymous — aggregate only"
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: "via",
      label: "Targeted via",
      width: "22%",
      render: (s) => {
        if (!s.notificationId) return "—";
        const linked = notifById.get(s.notificationId);
        const audienceId = linked?.notification.audienceId;
        if (!audienceId) return "—";
        return (
          <AttributionChip
            eventId={eventId}
            attribution={traceMembership(audienceId, traceIndex)}
          />
        );
      },
    },
  ];
  return (
    <AttendeeReportSection title="Surveys" count={surveys.length}>
      <DataTable<Survey>
        columns={columns}
        data={surveys}
        loading={false}
        ariaLabel="Surveys"
        emptyMessage="No surveys target this attendee."
        getRowKey={(s) => s.id}
        onRowClick={(s) =>
          window.location.assign(`/events/${eventId}/analytics/surveys/${s.id}`)
        }
      />
    </AttendeeReportSection>
  );
}

function AudiencesSection({
  eventId,
  audiences,
  personalAudienceId,
}: {
  eventId: string;
  audiences: Audience[];
  personalAudienceId: string;
}) {
  const named = audiences.filter((a) => a.name);
  const anonymousCount = audiences.filter(
    (a) => a.name === null && a.id !== personalAudienceId,
  ).length;
  const hasPersonal = audiences.some((a) => a.id === personalAudienceId);

  const columns: Column<Audience>[] = [
    {
      id: "name",
      label: "Audience",
      render: (a) => a.name ?? "(unnamed)",
    },
    {
      id: "kind",
      label: "Kind",
      width: "18%",
      render: (a) => (a.system ? "Role audience" : "Custom group"),
    },
    {
      id: "members",
      label: "Members",
      width: "12%",
      align: "right",
      render: (a) => (a.resolvedPersonIds ?? []).length,
    },
  ];

  return (
    <AttendeeReportSection
      title="Groups & audiences"
      count={named.length + (hasPersonal ? 1 : 0) + (anonymousCount > 0 ? 1 : 0)}
      subtitle={
        anonymousCount > 0
          ? `Plus ${anonymousCount} anonymous composition${anonymousCount === 1 ? "" : "s"} (visible inline as attribution chips above).`
          : undefined
      }
    >
      <DataTable<Audience>
        columns={columns}
        data={named}
        loading={false}
        ariaLabel="Named audiences"
        emptyMessage="No named audiences."
        getRowKey={(a) => a.id}
        onRowClick={(a) =>
          !a.system &&
          window.location.assign(`/events/${eventId}/groups/${a.id}`)
        }
      />
    </AttendeeReportSection>
  );
}
